import { Button, Input, Select } from '@aecom-ai-engineering/react-components';
import { useCreateProjectMutation, useMyUser } from '@consigli/hooks';
import {
  type Country,
  type CreateProjectRequest,
  isSharedOracleProject,
  type OracleProject,
  type ProjectConstructionType,
} from '@consigli/types';
import clsx from 'clsx';
import { ChevronLeft, X } from 'lucide-react';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { OracleProjectField } from '@/components/oracle-project-field';
import { ProjectFolderTile } from '@/components/project-folder-tile';
import {
  duplicateNameMessage,
  isDuplicateName,
} from '@/features/project-creation/utils/duplicate-name';
import { incrementNameSuffix } from '@/features/project-creation/utils/project-name';
import { Route } from '@/routes';
import { isAecomUser } from '@/util/aecom';
import { LOCKED_FIELD } from '@/util/locked-field';
import { newProjectCountryOptions } from '@/util/project-fields';

import { BrandRail } from './brand-rail';
import { LabelledField } from './labelled-field';
import { LegalWarningPanel } from './legal-warning-panel';
import { ProjectSetupLoading } from './project-setup-loading';
import { ProjectTypeField } from './project-type-field';

/** The weight the redesign draws its lucide marks at. */
const ICON_STROKE_WIDTH = 1.75;

/**
 * The frame's 800px row, which holds a form column, 40 of gap and the 280px
 * warning panel. The column is `flex-1` rather than the 480px the frame
 * measures, so the two fixed numbers place the third.
 *
 * `max-w-` and not `w-`: below 800 the row is the width it is given, and the
 * container centres it either way.
 */
const ROW = 'w-full max-w-[800px]';

/**
 * The country's clear button, in the chevron's place once a country is picked,
 * as the Oracle field swaps its own chevron for a clear. The library Select has
 * no clear of its own, so this sits over its trigger, which pads 12px: 12px
 * from the right lands it on the 16px chevron it replaces.
 */
const CLEAR_COUNTRY = [
  'absolute top-1/2 right-[var(--ds-space-3)] flex -translate-y-1/2 cursor-pointer items-center',
  'rounded-[var(--ds-radius-4)] text-[color:var(--ds-color-bg-icon-primary)]',
  'focus-visible:outline focus-visible:outline-[length:var(--ds-space-0-5)]',
  'focus-visible:outline-[var(--ds-color-border-primary)]',
  'focus-visible:outline-offset-[var(--ds-space-0-5)]',
].join(' ');

/** The five fields the frame draws, before anything is sent. */
interface CreateProjectDraft {
  address: string;
  constructionTypes: ProjectConstructionType[];
  /** `''` until a country is picked. `Country` has no member for "none". */
  country: Country | '';
  name: string;
  /** An Oracle project number, or `''`. See components/oracle-project-field.tsx. */
  oracleId: string;
}

const EMPTY_DRAFT: CreateProjectDraft = {
  address: '',
  constructionTypes: [],
  country: '',
  name: '',
  oracleId: '',
};

/**
 * What the picked Oracle project decides about the rest of the form. Held apart
 * from the draft because neither field is sent: the API stores no client name,
 * and the type class code only chooses how the name behaves.
 */
interface PickedOracleProject {
  customerName: string | null;
  /** Indirect or Bid & Proposal. See `isSharedOracleProject`. */
  shared: boolean;
}

/** The fields that can carry a message. Address is optional and cannot. */
type RequiredField = 'constructionTypes' | 'country' | 'name' | 'oracleId';

type FieldErrors = Partial<Record<RequiredField, string>>;

/**
 * The create request minus the one field neither frame draws.
 *
 * `invoiceTo` is required by `CreateProjectRequest` and is absent from both.
 * The legacy form registers it behind a billing step that has been commented
 * out since before this page existed, so every project created today reaches
 * the API without it and takes whatever default the serializer assigns. Naming
 * a value here would be this form settling a billing question nobody has put
 * to it, so the field is left off and the shape is asserted at the one call.
 */
type DrawnCreateProjectFields = Omit<CreateProjectRequest, 'invoiceTo'>;

/**
 * The redesigned create project screen: Figma 11914:189833, and 11914:190242
 * for the wait it becomes once **Create Project** is pressed.
 *
 * Behind `FeatureFlag.AppRedesign` — see project-creation-route.tsx.
 *
 * ## What it collects, and what it does not
 *
 * The frame's fields. The legacy form's billing step and its project
 * specifications are not drawn and are not sent.
 *
 * **Oracle ID** is the Oracle search rather than the plain box with
 * "( max 20 characters )" under it. See components/oracle-project-field.tsx,
 * which the project Settings tab renders for the same field and the same
 * reason.
 *
 * ## Oracle, project name and client name
 *
 * The same rules as the legacy form, features/project-creation/project-details.tsx:
 *
 * - Only an AECOM user sees Oracle ID and Client name, and only for them is an
 *   Oracle project required. The Oracle endpoints refuse anyone else.
 * - A Direct project fills the name from Oracle and locks it. Outside prod the
 *   override checkbox unlocks it. The Settings tab has no override.
 * - An Indirect or Bid & Proposal project is shared by many users, so the name
 *   starts blank and the user types their own. A name the API refuses as taken
 *   comes back with a " (2)" suffix to accept or edit.
 * - Client name is Oracle's customer name, read-only. The API has no field for
 *   it, so it is shown and not sent.
 *
 * ## Validation
 *
 * On submit, not on change, because the frame draws no error slot and a field
 * reddening under the cursor would be inventing one. Four of the five are
 * required by the API; address is not. Each message is its own `role="alert"`
 * and clears as soon as its own field changes.
 *
 * ## Why the page carries no app chrome
 *
 * Neither frame draws the side nav, the breadcrumb bar or the legacy page
 * header — the brand rail is the whole of the chrome. `useSuppressPageHeader`
 * in layouts/root-layout.tsx keeps the legacy header off this route while the
 * flag is on.
 */
export const CreateProjectPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useMyUser();
  const [createProject, { isLoading: isCreating }] = useCreateProjectMutation();

  const [draft, setDraft] = useState<CreateProjectDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [picked, setPicked] = useState<PickedOracleProject | null>(null);
  const [nameOverride, setNameOverride] = useState(false);
  /* Set when the API refused a shared project's name and a suffixed one was
     put in its place, so the field can say what happened. */
  const [nameClash, setNameClash] = useState<{ original: string; suggested: string } | null>(null);

  const aecomUser = isAecomUser(user.email);
  const shared = picked?.shared ?? false;
  // A Direct project's name is Oracle's. Only a non-prod build lets the
  // override unlock it, as in the legacy form.
  const isNameReadOnly = aecomUser && !shared && (import.meta.env.PROD || !nameOverride);

  const countries = useMemo(() => newProjectCountryOptions(t), [t]);
  /* Focused when the API refuses the name as a duplicate, which is the one
     failure the user fixes by typing in a field this page can reach. */
  const nameInput = useRef<HTMLInputElement>(null);

  /*
   * Null for a superuser, who belongs to no organisation and so has nothing to
   * create a project in. The legacy form asserts the field and reports the case
   * from inside its submit; this holds the send for the same reason
   * invite-users-panel.tsx holds its own — the request cannot go out naming an
   * organisation that is not there.
   */
  const organizationId = user.organizationId ?? undefined;

  const set = useCallback(
    <K extends keyof CreateProjectDraft>(key: K, value: CreateProjectDraft[K]) => {
      setDraft((current) => ({ ...current, [key]: value }));
      // The message answers the value that was there when submit ran, so it
      // stops being true the moment the field changes.
      setErrors((current) => {
        if (!(key in current)) return current;
        const next = { ...current };
        delete next[key as RequiredField];
        return next;
      });
    },
    [],
  );

  const leave = useCallback(() => navigate(`/${Route.PROJECTS}`), [navigate]);

  const onOracleProjectChange = useCallback(
    (project: OracleProject | null) => {
      const sharedProject = isSharedOracleProject(project?.projectTypeClassCode);
      setPicked(
        project === null ? null : { customerName: project.customerName, shared: sharedProject },
      );
      setNameClash(null);
      // A shared project's Oracle name is everyone's, so the user names it.
      set('name', project === null || sharedProject ? '' : project.projectName);
    },
    [set],
  );

  const clientName =
    draft.oracleId === ''
      ? ''
      : picked?.customerName?.trim() || t('project-creation.client-name-not-found');

  const onSubmit = useCallback(async () => {
    const country = draft.country === '' ? undefined : draft.country;
    const name = draft.name.trim();
    const found: FieldErrors = {};

    if (name === '') found.name = t('project-creation.project-name-required');
    if (aecomUser && draft.oracleId === '') {
      found.oracleId = t('project-creation.oracle-project-required');
    }
    if (country === undefined) found.country = t('project-creation.country-required');
    if (draft.constructionTypes.length === 0) {
      found.constructionTypes = t('project-creation.construction-type-required');
    }

    setErrors(found);

    // Each message is its own `role="alert"`, so they announce themselves.
    // Nothing is focused: of the four fields only the name is an input this
    // page holds a handle on, and moving focus to it when the country is the
    // thing missing would be worse than moving it nowhere.
    if (country === undefined || Object.keys(found).length > 0) return;

    if (organizationId === undefined) {
      toast.error(t('project-creation.not-belong-to-organisation'));
      return;
    }

    const request: DrawnCreateProjectFields = {
      // Trimmed, because trailing whitespace is not part of a project's name
      // and would make two identical names look different to the uniqueness
      // check.
      name,
      // Nullable on the wire, so cleared means null rather than "".
      address: draft.address.trim() || null,
      country,
      constructionTypes: draft.constructionTypes,
      oracleId: draft.oracleId === '' ? null : draft.oracleId,
      // Neither is drawn. An explicit empty list rather than a missing key:
      // both are arrays on the model, and "none chosen" is what the frame
      // means by not asking.
      specifications: [],
      availableServicesOverride: [],
      // The legacy form sends null for all three whenever its billing step is
      // not filled in, which is always.
      billingAddress: null,
      billingInformation: null,
      orgNumber: null,
      organizationId,
    };

    setNameClash(null);

    try {
      const project = await createProject(request as CreateProjectRequest).unwrap();

      toast.success(t('project-creation.success', { projectName: project.name }));
      navigate(`/${Route.PROJECTS}/${project.id}/${Route.AGENTS}`);
    } catch (error: unknown) {
      const duplicate = isDuplicateName(error);

      if (duplicate && shared) {
        const suggested = incrementNameSuffix(name);
        set('name', suggested);
        setNameClash({ original: name, suggested });
        nameInput.current?.focus();
        nameInput.current?.select();
        toast.info(
          t('project-creation.project-name-suggested-unique', { original: name, suggested }),
        );
        return;
      }

      if (!duplicate) {
        toast.error(t('project-creation.failure'));
        return;
      }

      const message = duplicateNameMessage(error, t);

      setErrors({ name: message });
      nameInput.current?.focus();
      toast.error(message);
    }
  }, [aecomUser, createProject, draft, navigate, organizationId, set, shared, t]);

  return (
    /*
     * A viewport-height row that does not scroll, holding a column that does.
     *
     * `h-screen` rather than `flex-1`: the scroll container needs a definite
     * height to scroll against, and root-layout.tsx gives this a `min-h-screen`
     * chain, which is a floor and not a height. `overflow-hidden` is what keeps
     * the document itself from scrolling once the form runs past the fold, so
     * the rail beside it cannot be scrolled away.
     *
     * The design system's body font at this root, as AppShell sets it: the DS
     * component sheets are split between inheriting the family and hardcoding
     * Switzer, and this page renders outside that shell.
     */
    <div className="ds-body-font flex h-screen overflow-hidden">
      <BrandRail linksHome={!isCreating} />

      {/* The one landmark on the page — neither AppShell nor the legacy header
          renders on this route, so there is no second <main> to nest inside.
          It owns the region's fill and its scrolling; both the form and the
          wait render into it. `relative` makes it the containing block for the
          absolutely positioned `sr-only` radio inputs: without it they resolve
          against root-layout.tsx's wrapper, escape this scroller's clip and
          stretch the document past the viewport, so the rail scrolls away. */}
      <main className="relative flex min-w-0 flex-1 flex-col overflow-y-auto bg-[var(--ds-color-bg-primary)]">
        {isCreating ? (
          <ProjectSetupLoading />
        ) : (
          <div className="flex w-full flex-col items-center px-6 py-14 lg:px-28">
            <div className={`${ROW} flex flex-col gap-10 lg:flex-row lg:items-start`}>
              <form
                className="flex min-w-0 flex-1 flex-col gap-6"
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  void onSubmit();
                }}
              >
                {/* A button, not a link: it goes back to the project list,
                    which is also where Cancel goes. 13/16 at weight 500 in the
                    frame's brand green. */}
                <button
                  className="flex w-fit cursor-pointer items-center gap-1.5 text-[13px] font-medium leading-4 text-[var(--ds-color-text-brand)]"
                  onClick={leave}
                  type="button"
                >
                  <ChevronLeft
                    aria-hidden="true"
                    className="size-4"
                    strokeWidth={ICON_STROKE_WIDTH}
                  />
                  {t('project-creation.back')}
                </button>

                <div className="flex items-start gap-4">
                  <ProjectFolderTile />

                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    {/* The frame's `heading 4`: 20/24 at weight 500. */}
                    <h1 className="text-xl font-medium leading-6 text-[var(--ds-color-text-primary)]">
                      {t('project-creation.page-title')}
                    </h1>
                    <p className="text-[13px] leading-5 text-[var(--ds-color-raw-stone-600)]">
                      {t('project-creation.page-subtitle')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-6">
                  {aecomUser && (
                    /* The frame's helper here is "…AND Task Number ( max 20
                       characters )", which describes a box you type into. This
                       is the Oracle search, so the hint is the one that search
                       ships — the same substitution settings-tab.tsx makes, for
                       the reason components/oracle-project-field.tsx gives. */
                    <LabelledField
                      description={t('project-creation.oracle-project-hint')}
                      descriptionId="create-project-oracle-id-help"
                      error={errors.oracleId}
                      label={t('project-creation.oracle-id-label')}
                    >
                      <OracleProjectField
                        describedBy="create-project-oracle-id-help"
                        label={t('project-creation.oracle-id-label')}
                        onChange={(projectNumber) => set('oracleId', projectNumber ?? '')}
                        onProjectChange={onOracleProjectChange}
                        value={draft.oracleId}
                      />
                    </LabelledField>
                  )}

                  <LabelledField
                    error={errors.name}
                    label={t('project-creation.project-name-label')}
                  >
                    {/* Read-only rather than disabled: the design system's
                        disabled text is --ds-color-text-disabled, too faint to
                        read the Oracle name by. */}
                    <Input
                      aria-describedby={aecomUser ? 'create-project-name-help' : undefined}
                      className={isNameReadOnly ? LOCKED_FIELD : undefined}
                      invalid={errors.name !== undefined}
                      label={t('project-creation.project-name-label')}
                      onChange={(event) => {
                        setNameClash(null);
                        set('name', event.target.value);
                      }}
                      placeholder={
                        isNameReadOnly ? undefined : t('project-creation.project-name-placeholder')
                      }
                      readOnly={isNameReadOnly}
                      ref={nameInput}
                      size="md"
                      value={draft.name}
                    />
                    {aecomUser && (
                      <div className="flex flex-wrap items-center gap-2">
                        <p
                          className="text-xs leading-4 text-[var(--ds-color-raw-stone-600)]"
                          id="create-project-name-help"
                        >
                          {t(
                            shared
                              ? 'project-creation.project-name-shared-hint'
                              : 'project-creation.project-name-auto-filled',
                          )}
                        </p>
                        {/* The legacy form's rule: shown outside prod, and to a
                            superuser in prod, where it unlocks nothing. */}
                        {!shared && (!import.meta.env.PROD || user.isSuperuser) && (
                          <label className="flex cursor-pointer items-center gap-1 text-xs leading-4 text-[var(--ds-color-raw-stone-600)] select-none">
                            <input
                              checked={nameOverride}
                              className="cursor-pointer"
                              onChange={(event) => setNameOverride(event.target.checked)}
                              type="checkbox"
                            />
                            {t('project-creation.project-name-override')}
                          </label>
                        )}
                      </div>
                    )}
                    {nameClash !== null && (
                      <p className="text-xs leading-4 text-[var(--ds-color-text-primary)]">
                        {t('project-creation.project-name-suggested-unique', nameClash)}
                      </p>
                    )}
                  </LabelledField>

                  {aecomUser && (
                    <LabelledField label={t('project-creation.client-name-label')}>
                      <Input
                        className={LOCKED_FIELD}
                        label={t('project-creation.client-name-label')}
                        readOnly
                        size="md"
                        tabIndex={-1}
                        value={clientName}
                      />
                    </LabelledField>
                  )}

                  <LabelledField label={t('project-creation.project-address-label')}>
                    <Input
                      label={t('project-creation.project-address-label')}
                      onChange={(event) => set('address', event.target.value)}
                      placeholder={t('project-creation.project-address-placeholder')}
                      size="md"
                      value={draft.address}
                    />
                  </LabelledField>

                  <LabelledField
                    description={t('project-creation.country-help-text')}
                    descriptionId="create-project-country-help"
                    error={errors.country}
                    label={t('project-creation.country-label')}
                  >
                    <div className="relative">
                      <Select
                        aria-describedby="create-project-country-help"
                        // Select.css caps its root at `min(100%, --ds-space-52)`
                        // — 208px, sized for the project toolbar. A utility
                        // rather than a CSS override: Tailwind's utilities layer
                        // sorts after the design system's components layer, so
                        // this wins on layer order without out-specifying
                        // anything. `menuWidth="trigger"` carries the menu with
                        // it.
                        //
                        // With a country picked, the chevron is hidden and the
                        // clear below takes its place. `invisible` rather than
                        // removed, so the value keeps its room beside it. Ark's
                        // attributes, not `data-ds-select-indicator`:
                        // scripts/check-css-layers.mjs fails any `[data-ds-*]`
                        // rule outside layer(components).
                        className={clsx(
                          'w-full',
                          draft.country !== '' &&
                            '[&_[data-scope=select][data-part=indicator]]:invisible',
                        )}
                        // Picking the selected country again also removes it.
                        deselectable
                        indicator="single"
                        invalid={errors.country !== undefined}
                        items={countries}
                        label={t('project-creation.country-label')}
                        menuWidth="trigger"
                        onValueChange={({ value }) =>
                          set('country', (value[0] as Country | undefined) ?? '')
                        }
                        placeholder={t('project-creation.country-placeholder')}
                        value={draft.country === '' ? [] : [draft.country]}
                      />
                      {draft.country !== '' && (
                        <button
                          aria-label={t('project-creation.country-clear')}
                          className={CLEAR_COUNTRY}
                          onClick={() => set('country', '')}
                          type="button"
                        >
                          <X
                            aria-hidden="true"
                            className="size-4"
                            strokeWidth={ICON_STROKE_WIDTH}
                          />
                        </button>
                      )}
                    </div>
                  </LabelledField>

                  <LabelledField
                    error={errors.constructionTypes}
                    label={t('project-creation.construction-type-label')}
                  >
                    <ProjectTypeField
                      onChange={(next) => set('constructionTypes', next)}
                      value={draft.constructionTypes}
                    />
                  </LabelledField>
                </div>

                {/* Right-aligned, as the frame places the pair. */}
                <div className="flex items-center justify-end gap-2">
                  <Button onClick={leave} size="md" variant="outline">
                    {t('project-creation.cancel')}
                  </Button>
                  {/* Held for a superuser, who has no organisation to create
                      the project in. */}
                  <Button
                    disabled={organizationId === undefined}
                    size="md"
                    type="submit"
                    variant="solid"
                  >
                    {t('project-creation.create-project')}
                  </Button>
                </div>
              </form>

              <LegalWarningPanel />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
